/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Değişiklik gerekiyorsa Core'da güncellenir ve senkron ile diğer repolara yayılır.
 */

export interface CreateUserDateTimeFormatterOptions {
  /**
   * Locale code (see SUPPORTED_LANGUAGES in language.utils).
   * Default: DEFAULT_LANGUAGE.
   */
  language?: string | null;
  /**
   * IANA timezone (örn: 'Europe/Istanbul'). Null ise browser timezone kullanılır.
   */
  timeZone?: string | null;
  /**
   * Tarih formatı pattern'i. Desteklenen token'lar: DD, MM, YYYY, YY
   * Örn: 'DD.MM.YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD'
   */
  dateFormat?: string | null;
  /**
   * Saat formatı. Varsayılan: 24h.
   */
  timeFormat?: string | null;
}

import { DEFAULT_LANGUAGE, type SupportedLanguage, SUPPORTED_LANGUAGES } from './language.utils';

const LANGUAGE_LOCALE_MAP: Record<SupportedLanguage, string> = {
  tr: 'tr-TR',
  en: 'en-US',
  ar: 'ar',
  de: 'de-DE',
  fr: 'fr-FR',
  es: 'es-ES',
  it: 'it-IT',
  ru: 'ru-RU',
  zh: 'zh-CN',
  ja: 'ja-JP',
};

const DEFAULT_DATE_FORMATS: Record<SupportedLanguage, string> = {
  tr: 'DD.MM.YYYY',
  en: 'MM/DD/YYYY',
  ar: 'DD/MM/YYYY',
  de: 'DD.MM.YYYY',
  fr: 'DD/MM/YYYY',
  es: 'DD/MM/YYYY',
  it: 'DD/MM/YYYY',
  ru: 'DD.MM.YYYY',
  zh: 'YYYY-MM-DD',
  ja: 'YYYY/MM/DD',
};

export interface UserDateTimeFormatter {
  language: SupportedLanguage;
  timeZone: string | null;
  dateFormat: string;
  timeFormat: '12h' | '24h';
  formatDate: (value: Date | string | number | null | undefined) => string;
  /** Kısa tarih (gün + kısa ay); takvim/board özetleri için */
  formatDateShort: (value: Date | string | number | null | undefined) => string;
  /** Ay ve yıl (başlık satırları) */
  formatMonthYear: (value: Date | string | number | null | undefined) => string;
  /** Haftanın günü kısaltması */
  formatWeekday: (value: Date | string | number | null | undefined) => string;
  formatTime: (
    value: Date | string | number | null | undefined,
    options?: { showSeconds?: boolean }
  ) => string;
  formatDateTime: (
    value: Date | string | number | null | undefined,
    options?: { showSeconds?: boolean }
  ) => string;
  toDate: (value: Date | string | number | null | undefined) => Date | null;
}

function normalizeLanguageForDate(value: any): SupportedLanguage {
  const v = String(value ?? '').toLowerCase().substring(0, 2);
  if (SUPPORTED_LANGUAGES.includes(v as SupportedLanguage)) {
    return v as SupportedLanguage;
  }
  return DEFAULT_LANGUAGE;
}

function toLocale(language: SupportedLanguage): string {
  return LANGUAGE_LOCALE_MAP[language] || LANGUAGE_LOCALE_MAP[DEFAULT_LANGUAGE];
}

function normalizeDateFormat(value: string | null | undefined, language: SupportedLanguage): string {
  const v = typeof value === 'string' ? value.trim() : '';
  if (v) return v;
  return DEFAULT_DATE_FORMATS[language] || 'DD/MM/YYYY';
}

function normalizeTimeFormat(value: string | null | undefined): '12h' | '24h' {
  const v = typeof value === 'string' ? value.trim().toLowerCase() : '';
  return v === '12h' ? '12h' : '24h';
}

function toDate(value: Date | string | number | null | undefined): Date | null {
  if (value === null || value === undefined) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isFinite(d.getTime()) ? d : null;
}

type DateParts = {
  year: string;
  month: string;
  day: string;
  hour: string;
  minute: string;
  second: string;
  dayPeriod: string;
};

function getParts(date: Date, locale: string, timeZone?: string | null, hour12 = false): DateParts {
  const dtf = new Intl.DateTimeFormat(locale, {
    timeZone: timeZone || undefined,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12,
  });

  const parts = dtf.formatToParts(date);
  const pick = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? '';

  return {
    year: pick('year'),
    month: pick('month'),
    day: pick('day'),
    hour: pick('hour') === '24' ? '00' : pick('hour'),
    minute: pick('minute'),
    second: pick('second'),
    dayPeriod: pick('dayPeriod'),
  };
}

function formatDateByPattern(parts: DateParts, pattern: string): string {
  const yyyy = parts.year;
  const yy = yyyy.length >= 2 ? yyyy.slice(-2) : yyyy;
  // Not: String.prototype.replaceAll bazı TS target'larda yok; regex ile global replace kullanıyoruz.
  return pattern
    .replace(/YYYY/g, yyyy)
    .replace(/YY/g, yy)
    .replace(/DD/g, parts.day)
    .replace(/MM/g, parts.month);
}

export function createUserDateTimeFormatter(options: CreateUserDateTimeFormatterOptions): UserDateTimeFormatter {
  const language = normalizeLanguageForDate(options.language);
  const locale = toLocale(language);
  const timeZone = options.timeZone ? String(options.timeZone).trim() : null;
  const dateFormat = normalizeDateFormat(options.dateFormat, language);
  const timeFormat = normalizeTimeFormat(options.timeFormat);

  const formatDate = (value: Date | string | number | null | undefined): string => {
    const d = toDate(value);
    if (!d) return '-';
    const parts = getParts(d, locale, timeZone);
    return formatDateByPattern(parts, dateFormat);
  };

  const formatTime = (
    value: Date | string | number | null | undefined,
    options2?: { showSeconds?: boolean }
  ): string => {
    const d = toDate(value);
    if (!d) return '-';
    const parts = getParts(d, locale, timeZone, timeFormat === '12h');
    const showSeconds = Boolean(options2?.showSeconds);
    const time = showSeconds ? `${parts.hour}:${parts.minute}:${parts.second}` : `${parts.hour}:${parts.minute}`;
    return timeFormat === '12h' && parts.dayPeriod ? `${time} ${parts.dayPeriod}` : time;
  };

  const formatDateTime = (
    value: Date | string | number | null | undefined,
    options2?: { showSeconds?: boolean }
  ): string => {
    const d = toDate(value);
    if (!d) return '-';
    return `${formatDate(d)} ${formatTime(d, options2)}`;
  };

  const tz = timeZone || undefined;

  const formatDateShort = (value: Date | string | number | null | undefined): string => {
    const d = toDate(value);
    if (!d) return '-';
    return new Intl.DateTimeFormat(locale, {
      timeZone: tz,
      day: '2-digit',
      month: 'short',
    }).format(d);
  };

  const formatMonthYear = (value: Date | string | number | null | undefined): string => {
    const d = toDate(value);
    if (!d) return '-';
    return new Intl.DateTimeFormat(locale, {
      timeZone: tz,
      month: 'long',
      year: 'numeric',
    }).format(d);
  };

  const formatWeekday = (value: Date | string | number | null | undefined): string => {
    const d = toDate(value);
    if (!d) return '-';
    return new Intl.DateTimeFormat(locale, {
      timeZone: tz,
      weekday: 'short',
    }).format(d);
  };

  return {
    language,
    timeZone,
    dateFormat,
    timeFormat,
    formatDate,
    formatDateShort,
    formatMonthYear,
    formatWeekday,
    formatTime,
    formatDateTime,
    toDate,
  };
}
